import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, MenuItem, TextField, Tooltip } from "@mui/material";
import MaterialTable, { MTableAction } from "material-table";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import {
  AddAdjustmentComponentsAction,
  DeleteAdjustmentComponentsAction,
  GetAdjustmentComponentsAction,
  UpdateAdjustmentComponentsAction,
} from "../../../../../store/Finance/PayRollTemplate/Action";

const AdjustmentComponentsGrid = () => {
  const dispatch = useDispatch();
  const { adjustment_components_data, isLoadingAdjustmentComponents } =
    useSelector((state) => state.FinanceReducer?.PayrollTemplateReducer);

  const mappedData = (adjustment_components_data || [])?.map((item) => ({
    ...item,
    label: item?.post_adjustment_components?.label || "",
    component_type: item?.post_adjustment_components?.component_type || "",
  }));

  useEffect(() => {
    dispatch(GetAdjustmentComponentsAction());
  }, [dispatch]);

  return (
    <Box>
      <MaterialTable
        isLoading={isLoadingAdjustmentComponents}
        columns={[
          {
            title: "Title",
            cellStyle: { padding: "0px 10px 0px 13px" },
            field: "label",
            validate: (rowData) => {
              if (rowData?.tableData && rowData?.tableData?.editing === "delete") {
                  return ""; 
                }
                if (!rowData?.label?.trim()) {
                  return "Title cannot be empty";
                }

                return "";
            },
            editComponent: (props) => {
              return (
                <Box sx={{ width: "70%" }}>
                  <TextField
                    required={true}
                    variant="standard"
                    fullWidth
                    value={props?.value}
                    onChange={(e) => props.onChange(e.target.value)}
                  />
                </Box>
              );
            },
          },
          {
            title: "Type",
            cellStyle: { padding: "0px 10px 0px 13px" },
            field: "component_type",
            validate: (rowData) => {
              if (rowData?.tableData && rowData?.tableData?.editing === "delete") {
                  return ""; 
                }
              if (!rowData?.component_type) {
                return "Type cannot be empty";
              }
              return "";
            },
            render: (rowData) => {
              const type = rowData?.component_type;
              return type === "earning"
                ? "Earning"
                : type === "deduction"
                ? "Deduction"
                : "";
            },
            editComponent: (props) => {
              return (
              <Box sx={{ width: "50%" }}>
                <TextField
                  select
                  required
                  fullWidth
                  variant="standard"
                  value={props?.value || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    props.onChange(newValue);
                    if (newValue === "deduction") {
                      props.rowData.taxable_flag = false;
                      props.rowData.is_esic_applicable = false;
                      props.onRowDataChange({
                        ...props.rowData,
                        component_type: newValue,
                        taxable_flag: false,
                        is_esic_applicable: false
                      });
                    }
                  }}
                >
                  <MenuItem value="earning">Earning</MenuItem>
                  <MenuItem value="deduction">Deduction</MenuItem>
                </TextField>
              </Box>
            )},
          },
          {
            title: "Is Taxable",
            cellStyle: { padding: "0px 10px 0px 15px" },
            field: "taxable_flag",
            render: (rowData) => (rowData?.taxable_flag ? "Yes" : "No"),
            editComponent: (props) => {
              const isDeduction = props.rowData?.component_type === "deduction";
              return (
               <Tooltip
                  title={isDeduction ? "Deductions are not taxable" : ""}
                  placement="top"
                >
                  <span>
                    <Checkbox
                      checked={Boolean(props?.value)}
                      onChange={(e) => props.onChange(e.target.checked)}
                      inputProps={{ "aria-label": "Taxable Checkbox" }}
                      disabled={isDeduction}
                    />
                  </span>
                </Tooltip>
            )},
          },
          {
            title: "Is ESIC Applicable",
            cellStyle: { padding: "0px 10px 0px 15px" },
            field: "is_esic_applicable",
            render: (rowData) => (rowData?.is_esic_applicable ? "Yes" : "No"),
            editComponent: (props) => {
              const isDeduction = props.rowData?.component_type === "deduction";
              return (
               <Tooltip
                  title={isDeduction ? "ESIC is not applicable on deduction components." : ""}
                  placement="top"
                >
                  <span>
                    <Checkbox
                      checked={Boolean(props?.value)}
                      onChange={(e) => props.onChange(e.target.checked)}
                      inputProps={{ "aria-label": "ESIC Checkbox" }}
                      disabled={isDeduction}
                    />
                  </span>
                </Tooltip>
            )},
          },
        ]}
        data={mappedData}
        icons={{
          Add: () =>( 
            <Button
                variant="contained" 
                className="mpl-primary-btn"
                style={{ minWidth: 0,width: "82px",height:"35px", padding: "2px 12px", textTransform: "none" }}
                startIcon={<AddIcon />}
              >
                Add
            </Button>
            ),
          Edit: (props) => <EditIcon sx={{ color: props?.disabled ? "#bdbdbd" : "#637381"}}/>,
          Delete: (props) => <DeleteIcon sx={{ color: props?.disabled ? "#bdbdbd" : "#637381"}} />,
        }}
        editable={{
          isDeletable: (rowData) => !rowData.is_default,
          isEditable: (rowData) => !rowData.is_default,
          onRowAdd: (newRow) =>
            new Promise((resolve, reject) => {
              if (!newRow?.label?.trim()) {
                return reject();
              }
              if (!newRow?.component_type) {
                return reject();
              }
              dispatch(
                AddAdjustmentComponentsAction({
                  ...newRow,
                  post_adjustment_components: JSON.stringify(
                    {
                      label: newRow?.label,
                      component_type: newRow?.component_type,
                      name: newRow?.label?.toLowerCase()?.replace(/ /g, "_"),
                    }
                  ),
                })
              );
              setTimeout(() => resolve(), 500);
            }),
          onRowUpdate: (newRow, oldRow) =>
            new Promise((resolve, reject) => {
              if (!newRow?.label?.trim()) {
                return reject();
              }
              if (!newRow?.component_type) {
                return reject();
              }
              dispatch(UpdateAdjustmentComponentsAction({
                ...newRow,
                post_adjustment_components: JSON.stringify(
                    {
                      label: newRow?.label,
                      component_type: newRow?.component_type,
                      name: newRow?.label?.toLowerCase()?.replace(/ /g, "_"),
                    },
                  ),
              }));
              setTimeout(() => resolve(), 500);
            }),
          onRowDelete: (selectedRow) =>
            new Promise((resolve, reject) => {
              dispatch(
                DeleteAdjustmentComponentsAction({
                  ...selectedRow,
                })
              );
              setTimeout(() => resolve(), 1000);
            }),
        }}
        options={{
          maxBodyHeight: 220,
          rowStyle: { fontSize: "13px" },
          headerStyle: { padding: "5px 10px 5px 13px", fontWeight: "bold" },
          sorting: false,
          search: false,
          searchFieldAlignment: "right",
          searchAutoFocus: true,
          searchFieldVariant: "standard",
          filtering: false,
          paging: false,
          selection: false,
          actionsColumnIndex: -1,
          grouping: false,
          addRowPosition: "first",
        }}
         components={{
              Action: (props) => {
                  const ActionComponent = props?.components?.Action || MTableAction;

                  if (props?.action?.icon && typeof props?.action?.icon === "function") {
                      return (
                      <Box
                          sx={{
                          "& .MuiIconButton-root": {
                              "&:hover": {
                              backgroundColor: "transparent",
                              },
                          },
                          }}
                      >
                          <ActionComponent {...props} />
                      </Box>
                      );
                  
                  }
                  return <ActionComponent {...props} />;
              },
          }}
        title=""
      />
    </Box>
  );
};
export default AdjustmentComponentsGrid;
